import React from 'react';
import type { monaco } from 'react-monaco-editor';
import { EditorProps } from '../EditorV3';

interface UseEditorResizeProps {
  editor?: monaco.editor.IStandaloneCodeEditor | monaco.editor.IStandaloneDiffEditor;
  minHeight: EditorProps['minHeight'];
  maxHeight: EditorProps['maxHeight'];
}

const clampHeight = (height: number, minHeight: number, maxHeight: number) =>
  Math.max(minHeight ?? 0, Math.min(height, maxHeight ?? Infinity));

const useEditorResize = ({ editor, minHeight, maxHeight }: UseEditorResizeProps) => {
  React.useLayoutEffect(() => {
    if (!editor) {
      return;
    }

    const resizeEditor = () => {
      let contentHeight = 0;
      let editorDomElement;
      if ('getContentHeight' in editor) {
        editorDomElement = editor.getDomNode();
        contentHeight = editor.getContentHeight();
      } else {
        editorDomElement = editor.getContainerDomNode();
        contentHeight = Math.max(
          editor.getOriginalEditor().getContentHeight(),
          editor.getModifiedEditor().getContentHeight(),
        );
      }
      const height = clampHeight(contentHeight, minHeight, maxHeight);
      editorDomElement.style.height = `${height}px`;
      editor.layout({ width: editorDomElement.clientWidth, height });
    };

    resizeEditor();
    if ('getContentHeight' in editor) {
      editor.onDidContentSizeChange(resizeEditor);
    } else {
      editor.getModifiedEditor().onDidContentSizeChange(resizeEditor);
    }
  }, [editor, minHeight, maxHeight]);
};

export default useEditorResize;
